/**
 * Backend API Helper Utility
 * Provides resilient fetch wrappers that try configured primary backend URLs
 * and fallback local/origin endpoints if the primary backend is unreachable.
 * Also supports attaching the current Supabase session's access token as a
 * Bearer Authorization header for admin-gated backend routes.
 */

import { supabase } from '../supabaseClient';

/**
 * Resolves candidate backend URLs based on environment variables,
 * window origin, and standard local development ports.
 * @returns {Array<string>} Unique list of non-empty base URLs
 */
export const getBackendBaseUrls = () => {
  let configuredUrl = process.env.REACT_APP_BACKEND_URL || process.env.VITE_APP_BACKEND_URL;
  if (configuredUrl && !configuredUrl.startsWith('http://') && !configuredUrl.startsWith('https://')) {
    configuredUrl = `https://${configuredUrl}`;
  }

  const urls = [configuredUrl];

  if (typeof window !== 'undefined') {
    // Add current origin (e.g. proxying setup)
    urls.push(window.location.origin);
    // Add same-hostname on default backend port 8000
    urls.push(`${window.location.protocol}//${window.location.hostname}:8000`);
    // Add standard localhost fallback
    urls.push('http://localhost:8000');
  }

  // Filter out undefined/null values and deduplicate
  return [...new Set(urls.filter(Boolean))];
};

/**
 * Fetches the current Supabase session's access token and returns it as an
 * Authorization header object, or {} if there is no active session.
 * @returns {Promise<Object>} { Authorization: 'Bearer <token>' } or {}
 */
export const getAuthHeader = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return {};
  return { Authorization: `Bearer ${session.access_token}` };
};

/**
 * Performs a fetch request against candidate backend URLs sequentially until one succeeds.
 * @param {string} path - API endpoint path (e.g. '/api/events')
 * @param {Object} [options] - Fetch request configuration options.
 *   Pass { requireAuth: true } to attach the current Supabase session's
 *   Bearer token — required for any endpoint using verify_admin_token.
 * @returns {Promise<Response>} HTTP Response object from successful endpoint
 * @throws {Error} Last encountered error if all endpoints fail
 */
export const fetchWithBackendFallback = async (path, options = {}) => {
  let lastError = null;
  const { requireAuth, headers, ...restOptions } = options;
  const authHeaders = requireAuth ? await getAuthHeader() : {};

  for (const baseUrl of getBackendBaseUrls()) {
    try {
      const response = await fetch(`${baseUrl}${path}`, {
        ...restOptions,
        headers: { ...headers, ...authHeaders },
      });
      if (!response.ok) {
        throw new Error(`Request failed against ${baseUrl}${path}`);
      }
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('text/html')) {
        throw new Error(`HTML response received from ${baseUrl}${path}`);
      }
      return response;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error(`Unable to reach backend for ${path}`);
};

/**
 * Performs a fetch request and automatically parses JSON response, falling back across backend URLs.
 * @param {string} path - API endpoint path
 * @param {Object} [options] - Fetch request options.
 *   Pass { requireAuth: true } to attach the current Supabase session's
 *   Bearer token — required for any endpoint using verify_admin_token.
 * @returns {Promise<Object>} Parsed JSON response body
 * @throws {Error} Error if all endpoints fail or non-JSON content is returned
 */
export const fetchJsonWithBackendFallback = async (path, options = {}) => {
  let lastError = null;
  const { requireAuth, headers, ...restOptions } = options;
  const authHeaders = requireAuth ? await getAuthHeader() : {};

  for (const baseUrl of getBackendBaseUrls()) {
    try {
      const response = await fetch(`${baseUrl}${path}`, {
        ...restOptions,
        headers: { ...headers, ...authHeaders },
      });

      if (!response.ok) {
        throw new Error(`Request failed against ${baseUrl}${path}`);
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('text/html')) {
        throw new Error(`HTML response received from ${baseUrl}${path}`);
      }

      return await response.json();
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error(`Unable to reach backend for ${path}`);
};